import { useEffect, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useGroupBuyStore } from '@/stores/useGroupBuyStore';

export function useGroupBuyUrlSync() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const store = useGroupBuyStore();
  const isInitialLoad = useRef(true);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Sync URL to store on initial load
  useEffect(() => {
    if (!isInitialLoad.current) return;

    // Get current filter values from URL
    const searchTerm = searchParams?.get('q') || '';
    const productType = searchParams?.get('type') || undefined;
    const status = searchParams?.get('status') || 'active';
    const sortBy = searchParams?.get('sort') || 'ending_soonest';

    const priceMin = searchParams?.get('priceMin')
      ? parseFloat(searchParams.get('priceMin')!)
      : undefined;
    const priceMax = searchParams?.get('priceMax')
      ? parseFloat(searchParams.get('priceMax')!)
      : undefined;

    const targetQuantityMin = searchParams?.get('targetMin')
      ? parseFloat(searchParams.get('targetMin')!)
      : undefined;
    const targetQuantityMax = searchParams?.get('targetMax')
      ? parseFloat(searchParams.get('targetMax')!)
      : undefined;

    const progressMin = searchParams?.get('progressMin')
      ? parseFloat(searchParams.get('progressMin')!)
      : undefined;
    const progressMax = searchParams?.get('progressMax')
      ? parseFloat(searchParams.get('progressMax')!)
      : undefined;

    const endingTimeframe = searchParams?.get('endingTimeframe') || undefined;

    const endingDateStr = searchParams?.get('endingDate');
    const endingDate = endingDateStr ? new Date(endingDateStr) : undefined;

    const page = searchParams?.get('page') ? parseInt(searchParams.get('page')!) : 1;

    // Batch update store with URL values
    store.startBatchUpdate();
    store.setSearchTerm(searchTerm);
    store.setProductType(productType);
    store.setStatus(status);
    store.setSortBy(sortBy);
    store.setPriceRange(priceMin, priceMax);
    store.setTargetQuantityRange(targetQuantityMin, targetQuantityMax);
    store.setProgressRange(progressMin, progressMax);
    store.setEndingTimeframe(endingTimeframe);
    store.setEndingDate(endingDate);
    store.setCurrentPage(page);
    store.endBatchUpdate();

    isInitialLoad.current = false;
  }, [searchParams, store]);

  // Debounced sync store to URL when store changes
  useEffect(() => {
    const handleStoreChange = () => {
      // Clear any existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set a new timer for 300ms
      debounceTimerRef.current = setTimeout(() => {
        const state = useGroupBuyStore.getState();
        const params = new URLSearchParams(searchParams?.toString() || '');

        // Update URL params based on store state
        if (state.searchTerm) params.set('q', state.searchTerm);
        else params.delete('q');

        if (state.productType) params.set('type', state.productType);
        else params.delete('type');

        if (state.status && state.status !== 'active') params.set('status', state.status);
        else params.delete('status');

        if (state.sortBy && state.sortBy !== 'ending_soonest') params.set('sort', state.sortBy);
        else params.delete('sort');

        if (state.priceRange.min !== undefined)
          params.set('priceMin', state.priceRange.min.toString());
        else params.delete('priceMin');

        if (state.priceRange.max !== undefined)
          params.set('priceMax', state.priceRange.max.toString());
        else params.delete('priceMax');

        if (state.targetQuantityRange.min !== undefined)
          params.set('targetMin', state.targetQuantityRange.min.toString());
        else params.delete('targetMin');

        if (state.targetQuantityRange.max !== undefined)
          params.set('targetMax', state.targetQuantityRange.max.toString());
        else params.delete('targetMax');

        if (state.progressRange.min !== undefined)
          params.set('progressMin', state.progressRange.min.toString());
        else params.delete('progressMin');

        if (state.progressRange.max !== undefined)
          params.set('progressMax', state.progressRange.max.toString());
        else params.delete('progressMax');

        if (state.endingTimeframe) params.set('endingTimeframe', state.endingTimeframe);
        else params.delete('endingTimeframe');

        if (state.endingDate) params.set('endingDate', state.endingDate.toISOString());
        else params.delete('endingDate');

        if (state.currentPage > 1) params.set('page', state.currentPage.toString());
        else params.delete('page');

        // Update URL without causing a page reload
        router.push(`?${params.toString()}`, { scroll: false });
      }, 300); // 300ms debounce
    };

    // Subscribe to store changes
    const unsubscribe = useGroupBuyStore.subscribe(handleStoreChange);

    // Cleanup subscription and any pending debounce timer
    return () => {
      unsubscribe();
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [router, searchParams]);
}
